import { useState, useCallback, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  ChevronDown,
  ChevronRight,
  Copy,
  Check,
  Package,
  MoreHorizontal,
  ShoppingCart,
  Tag,
  Truck,
  RotateCcw,
  AlertTriangle,
  PackageX,
  ShieldCheck,
  MessageCircle,
} from 'lucide-react';
import {
  UnifiedOrder,
  OrderItem,
  SITE_COLORS,
  AMZ_STATUS_COLORS,
  ML_STATUS_COLORS,
  WH_STATUS_COLORS,
  WH_STATUS_LABELS,
} from './types';
import { OrderAccordion } from './OrderAccordion';
import { ndClaimChip, ND_CLAIM_TONE_CLASS } from './ndClaimView';
import { useIsMobile } from '../../hooks/useIsMobile';
import { BagBadge } from '../../components/BagBadge';
import { useUIStore } from '../../stores/uiStore';
import { Can, useCan } from '../../lib/rbac';

const DELIVERY_ISSUE_LABELS: Record<string, string> = {
  LATE_PROMISE: 'Sin fecha de entrega',
  OVERDUE: 'Atrasado',
  TRACKING_SILENT: 'Sin updates',
  RESCHEDULED: 'Re-agendado',
  EXCEPTION: 'Excepción',
  CANCELLED_BY_AMAZON: 'Cancelado x Amazon',
};

/* ── Helpers ─────────────────────────────────────────────── */

function formatCurrency(amount: number | null | undefined, currency = 'USD'): string {
  if (amount == null) return '--';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

function timeAgo(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diffMs = now - then;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'now';
  if (diffMin < 60) return `${diffMin}m`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH}h`;
  const diffD = Math.floor(diffH / 24);
  if (diffD < 7) return `${diffD}d`;
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function StatusDot({ color, label }: { color: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1 text-xs whitespace-nowrap">
      <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
      <span>{label}</span>
    </span>
  );
}

/* ── Primary action resolver ─────────────────────────────── */

/** Reclamo de no entregadas a ML: distintivo del expediente vigente (nada si no hay o ya cerró con plata/devuelto). */
function NdClaimBadge({ order }: { order: UnifiedOrder }) {
  const chip = ndClaimChip(order.nd_claim);
  if (!chip) return null;
  return (
    <span
      className={`inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded ${ND_CLAIM_TONE_CLASS[chip.tone]}`}
      title={chip.title}
    >
      <PackageX size={9} /> {chip.label}
    </span>
  );
}

function resolvePrimaryAction(order: UnifiedOrder) {
  // Never show purchase actions for cancelled orders
  if (order.status === 'cancelled') return null;

  // Envío retenido por cupo de importación del comprador: ML retiene el despacho
  // (venta paid, substatus receiver_insufficient_quota). Comprar en Amazon acá
  // solo genera errores/compras huérfanas — badge "Sin Cupo" en vez de acción.
  // Cuando ML levanta la retención (el substatus cambia), el botón vuelve solo.
  const quotaHold = order.shipments?.some(s => s.substatus === 'receiver_insufficient_quota');
  if (quotaHold) {
    return { label: 'quota_hold', action: 'quota_hold', icon: <PackageX size={13} />, color: 'var(--color-warning)', disabled: true };
  }

  const hasPending = order.purchases.some(p => p.amazon_status === 'pending_approval' || p.amazon_status === 'safeguard_hold');
  if (hasPending) return { label: 'approve', action: 'approve', icon: <Check size={13} />, color: 'var(--color-primary)' };

  const hasError = order.purchases.some(p => p.amazon_status === 'error' || p.amazon_status === 'failed');
  if (hasError) return { label: 'retry', action: 'retry', icon: <RotateCcw size={13} />, color: 'var(--color-warning)' };

  // Compra HECHA con oferta sustituida por Amazon (precio/entrega distintos): alguien tiene que
  // confirmarla o recomprar. El botón despliega el detalle, donde están las dos opciones.
  const hasReview = order.purchases.some(p => p.amazon_status === 'needs_review');
  if (hasReview) return { label: 'review', action: 'review', icon: <AlertTriangle size={13} />, color: AMZ_STATUS_COLORS.needs_review };

  // Don't show BUY if internal stock already covers the order
  const activeStock = (order.internal_purchases || []).filter(ip => ip.status !== 'cancelled');
  if (order.purchases.length === 0 && order.status === 'paid' && activeStock.length === 0) return { label: 'buy', action: 'buy', icon: <ShoppingCart size={13} />, color: 'var(--color-primary)' };

  return null;
}

/* ── Image cell ──────────────────────────────────────────── */

function OrderImage({ items }: { items: OrderItem[] }) {
  if (items.length <= 1) {
    return (
      <img
        src={items[0]?.thumbnail || ''}
        alt=""
        className="w-[72px] h-[72px] rounded-lg object-cover bg-[var(--color-bg)]"
        loading="lazy"
      />
    );
  }
  return (
    <div className="grid grid-cols-2 gap-0.5 w-[72px] h-[72px]">
      {items.slice(0, 4).map((item, i) => (
        <img key={i} src={item.thumbnail || ''} alt="" className="w-full h-full rounded-sm object-cover bg-[var(--color-bg)]" loading="lazy" />
      ))}
    </div>
  );
}

/* ── Pack items list ─────────────────────────────────────── */

function PackItemsList({ items }: { items: OrderItem[] }) {
  return (
    <tr>
      <td colSpan={4} className="px-4 py-2 bg-[var(--color-bg)]">
        <div className="space-y-1.5 ml-[88px]">
          {items.map((item, idx) => (
            <div key={idx} className="flex items-center gap-3 p-2 rounded-lg bg-[var(--color-surface)] border border-[var(--color-border)]">
              <img src={item.thumbnail || ''} alt="" className="w-10 h-10 rounded object-cover bg-[var(--color-bg)]" />
              <span className="text-sm text-[var(--color-text)] flex-1 truncate">{item.title || item.item_id}</span>
              <Can
                cap="global.view_money"
                fallback={<span className="text-xs text-[var(--color-text-muted)]">—</span>}
              >
                <span className="text-xs text-[var(--color-text-secondary)]">{formatCurrency(item.unit_price, item.currency_id)}</span>
              </Can>
              <span className={`text-xs ${item.quantity > 1 ? 'text-[var(--color-error)] font-bold bg-[var(--color-error-light)] px-1.5 py-0.5 rounded' : 'text-[var(--color-text-muted)]'}`}>x{item.quantity}</span>
              <a href={`https://www.amazon.com/dp/${item.seller_sku}`} target="_blank" rel="noopener noreferrer" className="text-xs font-mono text-[var(--color-primary)] hover:underline">{item.seller_sku}</a>
            </div>
          ))}
        </div>
      </td>
    </tr>
  );
}

/* ── Mobile Card Component ──────────────────────────────── */

function OrderCard({ order, onAction, selected, onSelect }: {
  order: UnifiedOrder;
  onAction: (action: string, order: UnifiedOrder, data?: any) => void;
  selected: boolean;
  onSelect: (id: number | string) => void;
}) {
  const { t } = useTranslation();
  const can = useCan();
  const [expanded, setExpanded] = useState(false);
  const sellerId = useUIStore(s => s.sellerId);
  const orderId = order.pack_id || order.ml_order_id;
  const isPack = order.type === 'pack' && order.items.length > 1;
  const totalQty = order.items.reduce((s, i) => s + i.quantity, 0);
  const primaryItem = order.items[0];
  const primaryAction = resolvePrimaryAction(order);

  const worstAmz = order.purchases.map(p => p.amazon_status).find(s => s === 'error' || s === 'failed')
    || order.purchases.map(p => p.amazon_status).find(s => s === 'needs_review')
    || order.purchases.map(p => p.amazon_status).find(s => s === 'pending_approval' || s === 'safeguard_hold')
    || order.purchases[0]?.amazon_status;
  const amzStatusColor = worstAmz ? (AMZ_STATUS_COLORS[worstAmz] || 'var(--color-text-muted)') : '';
  const issuePurchase = order.purchases.find(p => p.delivery_issue_kind);
  const hasReview = order.purchases.some(p => p.amazon_status === 'needs_review');

  return (
    <tr className={`border-b border-[var(--color-border)] ${hasReview ? 'bg-orange-50 dark:bg-orange-950/25' : ''}`}>
      <td colSpan={4} className="p-0">
        <div className="p-3 space-y-2">
          {/* Top row: checkbox + image + info + amount */}
          <div className="flex gap-2.5">
            <input
              type="checkbox"
              checked={selected}
              onChange={() => onSelect(orderId!)}
              className="w-4 h-4 mt-1 rounded border-[var(--color-border)] accent-[var(--color-primary)] shrink-0"
            />
            <img
              src={primaryItem?.thumbnail || ''}
              alt=""
              className="w-14 h-14 rounded-lg object-cover bg-[var(--color-bg)] shrink-0"
              loading="lazy"
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <span className="text-sm font-medium text-[var(--color-text)] line-clamp-2 leading-tight">
                  {isPack
                    ? t('orders.row.packItems', 'Package of {{count}} products', { count: order.items.length })
                    : (primaryItem?.title || primaryItem?.item_id || '--')}
                </span>
                <span className="text-sm font-semibold text-[var(--color-text)] whitespace-nowrap shrink-0">
                  {can('global.view_money') ? formatCurrency(order.net_revenue) : '—'}
                </span>
              </div>
              <div className="flex items-center gap-1.5 mt-1 text-xs text-[var(--color-text-muted)]">
                <span className="font-mono text-[var(--color-primary)]">#{orderId}</span>
                <span>·</span>
                <span>{order.buyer.name}</span>
                <span>·</span>
                <span>{timeAgo(order.date_created)}</span>
                {totalQty > 1 && (
                  <span className="text-[var(--color-error)] font-bold bg-[var(--color-error-light)] px-1 py-0.5 rounded text-[10px]">
                    x{totalQty}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Badges row */}
          <div className="flex items-center gap-1.5 flex-wrap ml-[26px]">
            {/* Country */}
            <img
              src={`https://flagcdn.com/20x15/${{ MLA: 'ar', MLM: 'mx', MLC: 'cl', MCO: 'co', MLU: 'uy', MLB: 'br' }[order.context_site] || 'xx'}.png`}
              alt={order.context_site}
              className="w-4 h-3 object-cover rounded-sm"
            />

            {/* ML shipment status */}
            {order.shipments[0] && (
              <StatusDot
                color={
                  order.shipments[0].status === 'delivered' ? 'var(--color-success)'
                  : order.shipments[0].status === 'shipped' ? 'var(--color-info)'
                  : order.shipments[0].status === 'ready_to_ship' ? 'var(--color-warning)'
                  : 'var(--color-text-muted)'
                }
                label={order.shipments[0].substatus || order.shipments[0].status}
              />
            )}

            {/* Amazon status */}
            {worstAmz && <StatusDot color={amzStatusColor} label={worstAmz} />}

            {/* Delivery issue */}
            {issuePurchase && (
              <span
                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 border border-amber-300 dark:border-amber-700"
                title={issuePurchase.delivery_issue_detail || ''}
              >
                <Truck size={10} />
                {DELIVERY_ISSUE_LABELS[issuePurchase.delivery_issue_kind || ''] || issuePurchase.delivery_issue_kind}
                {issuePurchase.amazon_order_id && (
                  <a
                    href={`https://www.amazon.com/gp/your-account/order-details?orderID=${issuePurchase.amazon_order_id}`}
                    target="_blank"
                    rel="noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="ml-1 underline"
                  >
                    AMZ↗
                  </a>
                )}
              </span>
            )}

            {/* Pack */}
            {isPack && (
              <span className="inline-flex items-center gap-0.5 text-[10px] font-medium px-1.5 py-0.5 rounded bg-[var(--color-primary-light)] text-[var(--color-primary)]">
                <Package size={9} /> PACK
              </span>
            )}

            {/* Claim */}
            {order.has_claim && (() => {
              const openClaim = order.claims.find(c => c.status === 'opened');
              const isOpen = !!openClaim;
              return (
                <span
                  className={`inline-flex items-center gap-0.5 text-[10px] font-medium px-1.5 py-0.5 rounded ${
                    isOpen ? 'bg-[var(--color-error-light)] text-[var(--color-error)]' : 'bg-[var(--color-bg)] text-[var(--color-text-muted)]'
                  }`}
                  onClick={(e) => { e.stopPropagation(); if (openClaim || order.claims[0]) onAction('open_claim', order, { claim: openClaim || order.claims[0] }); }}
                >
                  <AlertTriangle size={9} /> CLAIM
                </span>
              );
            })()}

            {/* Vigilancia Atrasados: ML entregó DESPUÉS de marcar reintegro / pérdida / stock */}
            {order.late_delivered_at && (
              <span
                className="inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded bg-red-600 text-white"
                title="Mercado Libre entregó este envío DESPUÉS de que se marcó reintegro, pérdida o conversión a stock — revisar el libro y el stock"
              >
                <AlertTriangle size={9} /> ENTREGADA TRAS MARCAR
              </span>
            )}

            {/* Not Delivered — incluye estado de la venta: PAID = plata sigue nuestra, CANCELLED = revisar reintegro.
                Variantes: DEVUELTO (producto en nuestro poder, libro en $0) y FRAUDE (ML canceló antes del courier — nada que reclamar) */}
            {order.shipments.some(s => s.status === 'not_delivered') && (
              order.returned_to_seller ? (
                <span
                  className="inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300"
                  title="El producto volvió al vendedor — libro compensado en $0"
                >
                  <ShieldCheck size={9} /> DEVUELTO{order.returned_to_seller_country ? ` · ${order.returned_to_seller_country.toUpperCase()}` : ''}
                </span>
              ) : order.shipments.some(s => s.status === 'not_delivered' && s.substatus === 'fraudulent') ? (
                <span
                  className="inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300"
                  title="ML canceló por fraude ANTES de entregar al correo — nada que reclamar"
                >
                  <PackageX size={9} /> CANCELADA ML · FRAUDE
                </span>
              ) : (
              <span
                className={`inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded ${
                  order.status === 'cancelled'
                    ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                    : 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300'
                }`}
                title={
                  order.status === 'cancelled'
                    ? 'Venta CANCELADA — verificar reintegro de ML'
                    : 'La venta sigue PAGADA — la plata es nuestra, NO reclamar reintegro salvo que ML cancele'
                }
              >
                <PackageX size={9} /> NOT DEL · {order.status === 'cancelled' ? 'CANCELLED' : order.status.toUpperCase()}
              </span>
              )
            )}

            {/* Reclamo de no entregadas a ML (etapa 2) */}
            <NdClaimBadge order={order} />

            {/* Warehouse */}
            {order.wh_status && (
              <span
                className="inline-flex items-center text-[10px] font-medium px-1.5 py-0.5 rounded"
                style={{
                  backgroundColor: `color-mix(in srgb, ${WH_STATUS_COLORS[order.wh_status]} 15%, transparent)`,
                  color: WH_STATUS_COLORS[order.wh_status],
                }}
              >
                {WH_STATUS_LABELS[order.wh_status]}
              </span>
            )}

            {/* Unread messages */}
            {order.unread_count > 0 && (
              <span className="inline-flex items-center gap-0.5 text-[10px] font-bold text-blue-600 dark:text-blue-400">
                <MessageCircle size={10} /> {order.unread_count}
              </span>
            )}

            {/* Bolsa WH (compact) */}
            {order.ml_order_id && (
              <BagBadge mlOrderId={order.ml_order_id} sellerId={sellerId} variant="compact" />
            )}

            {/* Profit */}
            {(order.amazon_cost > 0 || order.stock_cost > 0) && (
              <Can cap="global.view_money">
                <span className={`text-[10px] font-medium ml-auto ${(order.profit ?? 0) >= 0 ? 'text-[var(--color-success)]' : 'text-[var(--color-error)]'}`}>
                  {formatCurrency(order.profit)} {order.margin_pct != null && `(${order.margin_pct}%)`}
                </span>
              </Can>
            )}
          </div>

          {/* Action row */}
          <div className="flex items-center gap-2 ml-[26px]">
            {primaryAction && ('disabled' in primaryAction && primaryAction.disabled ? (
              <span
                title="Envío retenido por cupo de importación del comprador — la compra se habilita cuando ML libera el envío"
                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg text-white cursor-default"
                style={{ backgroundColor: primaryAction.color }}
              >
                {primaryAction.icon}
                {t(`orders.actions.${primaryAction.label}`, 'Sin Cupo')}
              </span>
            ) : (
              <Can cap="orders.buy">
                <button
                  onClick={(e) => { e.stopPropagation(); if (primaryAction.action === 'review') setExpanded(true); else onAction(primaryAction.action, order); }}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg text-white hover:opacity-90"
                  style={{ backgroundColor: primaryAction.color }}
                >
                  {primaryAction.icon}
                  {t(`orders.actions.${primaryAction.label}`, primaryAction.label)}
                </button>
              </Can>
            ))}
            <Can cap="orders.operate">
              <button
                onClick={(e) => { e.stopPropagation(); onAction('link', order); }}
                className="px-2.5 py-1.5 text-xs rounded-lg border border-[var(--color-border)] text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-hover)]"
              >
                {t('orders.actions.link')}
              </button>
            </Can>
            <Can cap="orders.messages">
              <button
                onClick={(e) => { e.stopPropagation(); onAction('send_message', order); }}
                className="px-2.5 py-1.5 text-xs rounded-lg border border-[var(--color-border)] text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-hover)]"
              >
                <MessageCircle size={12} />
              </button>
            </Can>
            <button
              onClick={() => setExpanded(v => !v)}
              className="ml-auto p-1.5 rounded-lg text-[var(--color-text-muted)] hover:bg-[var(--color-surface-hover)]"
            >
              {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            </button>
          </div>

          {/* Accordion */}
          {expanded && <OrderAccordion order={order} onAction={onAction} />}
        </div>
      </td>
    </tr>
  );
}

/* ── Main component ──────────────────────────────────────── */

interface OrderRowProps {
  order: UnifiedOrder;
  onAction: (action: string, order: UnifiedOrder, data?: any) => void;
  selected: boolean;
  onSelect: (id: number | string) => void;
}

export function OrderRow({ order, onAction, selected, onSelect }: OrderRowProps) {
  const { t } = useTranslation();
  const can = useCan();
  const isMobile = useIsMobile();
  const [expanded, setExpanded] = useState(false);
  const [packExpanded, setPackExpanded] = useState(false);
  const [copied, setCopied] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuBtnRef = useRef<HTMLButtonElement>(null);
  const [menuPos, setMenuPos] = useState({ top: 0, right: 0 });
  const sellerId = useUIStore(s => s.sellerId);

  const orderId = order.pack_id || order.ml_order_id;
  const isPack = order.type === 'pack' && order.items.length > 1;
  const totalQty = order.items.reduce((s, i) => s + i.quantity, 0);
  const primaryItem = order.items[0];

  const mlStatusColor = ML_STATUS_COLORS[order.status] || 'var(--color-text-muted)';
  const worstAmz = order.purchases.map(p => p.amazon_status).find(s => s === 'error' || s === 'failed')
    || order.purchases.map(p => p.amazon_status).find(s => s === 'needs_review')
    || order.purchases.map(p => p.amazon_status).find(s => s === 'pending_approval' || s === 'safeguard_hold')
    || order.purchases[0]?.amazon_status;
  const amzStatusColor = worstAmz ? (AMZ_STATUS_COLORS[worstAmz] || 'var(--color-text-muted)') : '';
  const hasReview = order.purchases.some(p => p.amazon_status === 'needs_review');
  const reviewRowBg = hasReview ? ' bg-orange-50 dark:bg-orange-950/25' : '';

  const firstTracking = order.purchases.find(p => p.tracking_number)?.tracking_number;
  const deliveryDate = order.purchases.find(p => p.delivery_date)?.delivery_date;
  const deliveryText = order.purchases.find(p => p.offer_delivery_text)?.offer_delivery_text;
  const isPrePurchase = worstAmz && ['pending_approval','safeguard_hold'].includes(worstAmz);
  const primaryAction = resolvePrimaryAction(order);
  const issuePurchase = order.purchases.find(p => p.delivery_issue_kind);

  // Mobile: render card view instead of table rows
  if (isMobile) {
    return <OrderCard order={order} onAction={onAction} selected={selected} onSelect={onSelect} />;
  }

  const handleCopyId = useCallback(() => {
    navigator.clipboard.writeText(String(orderId || ''));
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }, [orderId]);

  // Close menu on outside click
  const menuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node) &&
          menuBtnRef.current && !menuBtnRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  const toggleExpand = useCallback(() => setExpanded(v => !v), []);
  const togglePack = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setPackExpanded(v => !v);
  }, []);

  return (
    <>
      {/* Row 1: Product + Identity */}
      <tr
        className={`border-b-0 hover:bg-[var(--color-surface-hover)] cursor-pointer transition-colors${reviewRowBg}`}
        onClick={toggleExpand}
      >
        {/* Checkbox — spans 2 rows */}
        <td rowSpan={2} className="w-10 pl-3 py-2 align-middle">
          <input
            type="checkbox"
            checked={selected}
            onChange={() => onSelect(orderId!)}
            onClick={(e) => e.stopPropagation()}
            className="w-4 h-4 rounded border-[var(--color-border)] text-[var(--color-primary)] cursor-pointer accent-[var(--color-primary)]"
          />
        </td>

        {/* Image — spans 2 rows */}
        <td rowSpan={2} className="w-[88px] pl-2 py-2 align-middle">
          <OrderImage items={order.items} />
        </td>

        {/* Main content — spans 2 rows worth of info */}
        <td className="px-3 pt-2.5 pb-0">
          <div className="flex items-center gap-2 flex-wrap">
            {/* Product title */}
            <span className="text-sm font-medium text-[var(--color-text)] truncate max-w-[400px]">
              {isPack ? (
                <button onClick={togglePack} className="inline-flex items-center gap-1 hover:text-[var(--color-primary)]">
                  {packExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  {t('orders.row.packItems', 'Package of {{count}} products', { count: order.items.length })}
                </button>
              ) : (
                primaryItem?.title || primaryItem?.item_id || '--'
              )}
            </span>

            {/* Country flag */}
            <img
              src={`https://flagcdn.com/20x15/${{ MLA: 'ar', MLM: 'mx', MLC: 'cl', MCO: 'co', MLU: 'uy', MLB: 'br' }[order.context_site] || 'xx'}.png`}
              alt={order.context_site}
              title={order.context_site}
              className="w-5 h-[15px] object-cover rounded-sm"
            />

            {/* Pack badge */}
            {isPack && (
              <span className="inline-flex items-center gap-0.5 text-[10px] font-medium px-1.5 py-0.5 rounded bg-[var(--color-primary-light)] text-[var(--color-primary)]">
                <Package size={9} /> PACK
              </span>
            )}

            {/* Claim badge — show human-readable title, clickable to open panel */}
            {order.has_claim && (() => {
              const openClaim = order.claims.find(c => c.status === 'opened');
              const anyClaim = openClaim || order.claims[0];
              const claimLabel = openClaim?.detail_title || (openClaim ? 'CLAIM' : 'CLAIM (closed)');
              const isOpen = !!openClaim;
              return (
                <span
                  className={`inline-flex items-center gap-0.5 text-[10px] font-medium px-1.5 py-0.5 rounded max-w-[200px] truncate cursor-pointer hover:opacity-80 ${
                    isOpen ? 'bg-[var(--color-error-light)] text-[var(--color-error)]' : 'bg-[var(--color-bg)] text-[var(--color-text-muted)]'
                  }`}
                  title={claimLabel}
                  onClick={(e) => { e.stopPropagation(); if (anyClaim) onAction('open_claim', order, { claim: anyClaim }); }}
                >
                  <AlertTriangle size={9} className="flex-shrink-0" /> {claimLabel}
                </span>
              );
            })()}

            {order.late_delivered_at && (
              <span
                className="inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded bg-red-600 text-white"
                title="Mercado Libre entregó este envío DESPUÉS de que se marcó reintegro, pérdida o conversión a stock — revisar el libro y el stock"
              >
                <AlertTriangle size={9} /> ENTREGADA TRAS MARCAR
              </span>
            )}

            {/* Not Delivered badge — incluye estado de la venta: PAID = plata sigue nuestra, CANCELLED = revisar reintegro.
                Variantes: DEVUELTO AL VENDEDOR (producto en nuestro poder, libro en $0) y FRAUDE (ML canceló antes del courier) */}
            {order.shipments.some(s => s.status === 'not_delivered') && (
              order.returned_to_seller ? (
                <span
                  className="inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300"
                  title="El producto volvió al vendedor — libro compensado en $0"
                >
                  <ShieldCheck size={9} /> DEVUELTO AL VENDEDOR{order.returned_to_seller_country ? ` · ${order.returned_to_seller_country.toUpperCase()}` : ''}
                </span>
              ) : order.shipments.some(s => s.status === 'not_delivered' && s.substatus === 'fraudulent') ? (
                <span
                  className="inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300"
                  title="ML canceló por fraude ANTES de entregar al correo — nada que reclamar"
                >
                  <PackageX size={9} /> CANCELADA ML · FRAUDE
                </span>
              ) : (
              <span
                className={`inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded ${
                  order.status === 'cancelled'
                    ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                    : 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300'
                }`}
                title={
                  order.status === 'cancelled'
                    ? 'Venta CANCELADA — verificar reintegro de ML'
                    : 'La venta sigue PAGADA — la plata es nuestra, NO reclamar reintegro salvo que ML cancele'
                }
              >
                <PackageX size={9} /> NOT DELIVERED · {order.status === 'cancelled' ? 'CANCELLED' : order.status.toUpperCase()}
              </span>
              )
            )}

            {/* Reimbursed badge */}
            {order.seller_reimbursed && (
              <span className="inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300" title={`Ref: ${order.seller_reimbursed_ref || '-'} | ${order.seller_reimbursed_at ? new Date(order.seller_reimbursed_at).toLocaleDateString() : ''}`}>
                <ShieldCheck size={9} /> REINTEGRADO {order.seller_reimbursed_amount && can('global.view_money') ? `$${order.seller_reimbursed_amount.toFixed(2)}` : ''}
              </span>
            )}

            {/* Reclamo de no entregadas a ML (etapa 2) */}
            <NdClaimBadge order={order} />

            {/* Warehouse badge */}
            {order.wh_status && (
              <span
                className="inline-flex items-center gap-0.5 text-[10px] font-medium px-1.5 py-0.5 rounded"
                style={{
                  backgroundColor: `color-mix(in srgb, ${WH_STATUS_COLORS[order.wh_status]} 15%, transparent)`,
                  color: WH_STATUS_COLORS[order.wh_status],
                }}
              >
                {WH_STATUS_LABELS[order.wh_status]}{order.wh_batch_id ? ` #${order.wh_batch_id}` : ''}
              </span>
            )}
          </div>
        </td>

        {/* Amount + Actions — spans 2 rows */}
        <td rowSpan={2} className="px-3 py-2 align-middle text-right w-[160px]">
          <div className="flex flex-col items-end gap-1.5" onClick={e => e.stopPropagation()}>
            <div>
              <span className="text-sm font-semibold text-[var(--color-text)]">
                {can('global.view_money') ? formatCurrency(order.net_revenue) : '—'}
              </span>
              <span className={`text-xs ml-1 ${totalQty > 1 ? 'text-[var(--color-error)] font-bold bg-[var(--color-error-light)] px-1.5 py-0.5 rounded' : 'text-[var(--color-text-muted)]'}`}>x{totalQty}</span>
            </div>
            <div className="flex items-center gap-1">
              {primaryAction && ('disabled' in primaryAction && primaryAction.disabled ? (
                <span
                  title="Envío retenido por cupo de importación del comprador — la compra se habilita cuando ML libera el envío"
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg text-white cursor-default"
                  style={{ backgroundColor: primaryAction.color }}
                >
                  {primaryAction.icon}
                  {t(`orders.actions.${primaryAction.label}`, 'Sin Cupo')}
                </span>
              ) : (
                <Can cap="orders.buy">
                  <button
                    onClick={() => primaryAction.action === 'review' ? setExpanded(true) : onAction(primaryAction.action, order)}
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg text-white hover:opacity-90 transition-opacity"
                    style={{ backgroundColor: primaryAction.color }}
                  >
                    {primaryAction.icon}
                    {t(`orders.actions.${primaryAction.label}`, primaryAction.label)}
                  </button>
                </Can>
              ))}
              <div className="relative">
                <button
                  ref={menuBtnRef}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (menuBtnRef.current) {
                      const rect = menuBtnRef.current.getBoundingClientRect();
                      setMenuPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
                    }
                    setMenuOpen(v => !v);
                  }}
                  className="p-1.5 rounded-lg hover:bg-[var(--color-bg)] text-[var(--color-text-muted)] transition-colors"
                  title="More actions"
                >
                  <MoreHorizontal size={16} />
                </button>
              </div>
            </div>
          </div>
        </td>
      </tr>

      {/* Row 2: Buyer + Financials + Statuses */}
      <tr
        className={`border-b border-[var(--color-border)] hover:bg-[var(--color-surface-hover)] cursor-pointer transition-colors${reviewRowBg}`}
        onClick={toggleExpand}
      >
        <td className="px-3 pb-2.5 pt-0.5">
          <div className="flex items-center gap-3 text-xs text-[var(--color-text-muted)] flex-wrap">
            {/* Order ID */}
            <button
              onClick={(e) => { e.stopPropagation(); handleCopyId(); }}
              className="inline-flex items-center gap-0.5 font-mono text-[var(--color-primary)] hover:underline"
            >
              #{orderId}
              {copied ? <Check size={10} className="text-[var(--color-success)]" /> : <Copy size={10} className="opacity-40" />}
            </button>

            <span className="text-[var(--color-border)]">·</span>

            {/* Buyer */}
            <span className="inline-flex items-center gap-1">
              {order.buyer.name}
              {order.unread_count > 0 && (
                <span className="inline-flex items-center gap-0.5 text-xs font-bold text-blue-600 dark:text-blue-400" title={`${order.unread_count} mensaje(s) sin leer`}>
                  <MessageCircle size={12} />
                  {order.unread_count}
                </span>
              )}
            </span>

            <span className="text-[var(--color-border)]">·</span>

            {/* Time */}
            <span>{timeAgo(order.date_created)}</span>

            <span className="text-[var(--color-border)]">·</span>

            {/* Profit: net → cost → profit — sin la llave de dinero no se muestra */}
            {!can('global.view_money') ? (
              <span className="text-[var(--color-text-muted)]">—</span>
            ) : (order.amazon_cost > 0 || order.stock_cost > 0) ? (
              <span>
                <span className="opacity-60">cost</span> {formatCurrency(order.amazon_cost + (order.stock_cost || 0))} →{' '}
                <span className={(order.profit ?? 0) >= 0 ? 'text-[var(--color-success)] font-medium' : 'text-[var(--color-error)] font-medium'}>
                  {formatCurrency(order.profit)}
                </span>
                {order.margin_pct != null && <span className="ml-0.5 opacity-60">({order.margin_pct}%)</span>}
              </span>
            ) : (
              <span className="opacity-40">sin compra</span>
            )}

            <span className="text-[var(--color-border)]">·</span>

            {/* Shipment substatus (more useful than "paid") */}
            {order.shipments[0] && (
              <StatusDot
                color={
                  order.shipments[0].status === 'delivered' ? 'var(--color-success)'
                  : order.shipments[0].status === 'shipped' ? 'var(--color-info)'
                  : order.shipments[0].status === 'ready_to_ship' ? 'var(--color-warning)'
                  : 'var(--color-text-muted)'
                }
                label={order.shipments[0].substatus || order.shipments[0].status}
              />
            )}
            {worstAmz && (
              <StatusDot color={amzStatusColor} label={worstAmz} />
            )}
            {issuePurchase && (
              <span
                className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 border border-amber-300 dark:border-amber-700 whitespace-nowrap"
                title={issuePurchase.delivery_issue_detail || ''}
              >
                <Truck size={12} />
                {DELIVERY_ISSUE_LABELS[issuePurchase.delivery_issue_kind || ''] || issuePurchase.delivery_issue_kind}
                {issuePurchase.amazon_order_id && (
                  <a
                    href={`https://www.amazon.com/gp/your-account/order-details?orderID=${issuePurchase.amazon_order_id}`}
                    target="_blank"
                    rel="noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="ml-1 underline hover:opacity-80"
                  >
                    Amazon ↗
                  </a>
                )}
              </span>
            )}
            {deliveryDate && !firstTracking && (
              <span className="text-[var(--color-info)] whitespace-nowrap">
                ETD {deliveryText && isPrePurchase
                  ? deliveryText.replace(/^FREE delivery\s*/i, '')
                  : new Date(deliveryDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              </span>
            )}

            {/* Tracking */}
            {firstTracking && (
              <span className="font-mono truncate max-w-[90px]" title={firstTracking}>{firstTracking}</span>
            )}

            {/* Delivery — only show here when there's tracking (otherwise shown next to status) */}
            {deliveryDate && firstTracking && (
              <span className="whitespace-nowrap">
                Est. {new Date(deliveryDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              </span>
            )}

            {/* Bolsa WH (compact) */}
            {order.ml_order_id && (
              <BagBadge mlOrderId={order.ml_order_id} sellerId={sellerId} variant="compact" />
            )}

            {/* Expand indicator */}
            <span className="ml-auto">
              {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            </span>
          </div>
        </td>
      </tr>

      {/* Pack items expansion */}
      {isPack && packExpanded && <PackItemsList items={order.items} />}

      {/* Accordion expansion */}
      {expanded && (
        <tr>
          <td colSpan={4} className="p-0">
            <OrderAccordion order={order} onAction={onAction} />
          </td>
        </tr>
      )}

      {/* Dropdown menu — portaled to body to avoid table overflow clip */}
      {menuOpen && createPortal(
          <div
            ref={menuRef}
            className="fixed z-[9999] w-48 bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg shadow-lg py-1"
            style={{ top: menuPos.top, right: menuPos.right }}
          >
            {[
              { action: 'change_asin', label: t('orders.actions.change_asin'), caps: ['orders.edit_cost'] },
              { action: 'link', label: t('orders.actions.link'), caps: ['orders.operate'] },
              // La Compra Manual escribe costo unitario y envío: pide operar Y ver dinero.
              { action: 'generic_purchase', label: t('orders.actions.generic_purchase'), caps: ['orders.operate', 'global.view_money'] },
              // Crear una orden de compra sin ver costos cargaría costo cero: exige también ver dinero.
              { action: 'create_po', label: t('orders.actions.create_po'), caps: ['orders.operate', 'global.view_money'] },
              { action: 'edit_cost', label: t('orders.actions.edit_cost'), caps: ['orders.edit_cost'] },
              { action: 'unlink', label: t('orders.actions.unlink'), caps: ['orders.operate'] },
            ].filter(item => item.caps.every(c => can(c))).map(item => (
              <button
                key={item.action}
                onClick={(e) => { e.stopPropagation(); setMenuOpen(false); onAction(item.action, order); }}
                className="w-full text-left px-3 py-2 text-sm text-[var(--color-text)] hover:bg-[var(--color-surface-hover)] transition-colors"
              >
                {item.label}
              </button>
            ))}
            <div className="border-t border-[var(--color-border)] my-1" />
            <a
              href={`https://www.mercadolibre.com/ventas/${order.pack_id || order.ml_order_id}/detalle`}
              target="_blank"
              rel="noopener noreferrer"
              className="block px-3 py-2 text-sm text-[var(--color-primary)] hover:bg-[var(--color-surface-hover)] transition-colors"
              onClick={() => setMenuOpen(false)}
            >
              {t('orders.actions.view_ml')} ↗
            </a>
          </div>,
        document.body
      )}
    </>
  );
}
